package hk.com.vr360_demo2.webapp.component.viewer

import emotion.react.css
import hk.com.vr360_demo2.webapp.component.baseview.DefaultScreenUI
import hk.com.vr360_demo2.webapp.component.marzipano.PanoramaViewer
import hk.com.vr360_demo2.webapp.component.viewer.component.HeaderBar
import hk.com.vr360_demo2.webapp.component.viewer.component.ViewerBottomInfoOverlay
import kotlinx.browser.document
import kotlinx.browser.window
import react.FC
import react.Props
import react.dom.html.ReactHTML.div
import react.useEffect
import react.useLayoutEffect
import react.useState
import web.cssom.AlignItems
import web.cssom.Display
import web.cssom.FlexDirection
import web.cssom.JustifyContent
import web.cssom.Position
import web.cssom.pct
import web.cssom.vh
import kotlin.js.js
import react.CSSProperties

external interface ViewerProps : Props {
    var component: ViewerComponent
}

val ViewerScreen = FC<ViewerProps> {
    val (uiState, setUiState) = useState(ViewerState())

    useEffect {
        it.component.state.collect { setUiState(it) }
    }

    val data = uiState.panoramaData
    val hasReady = uiState.scenes.isNotEmpty()

    DefaultScreenUI {
        this.hasReady = hasReady
        this.progressBarState = uiState.progressBarState
        this.networkState = uiState.networkState
        this.errors = it.component.errors
        this.emptyMessage = "暫無全景數據"
        this.onTryAgain = { it.component.dispatch(ViewerIntent.Refresh) }

        if (hasReady && data != null) {
            // 浮層信息面板
            div {
                css {
                    position = Position.absolute
                    width = 100.pct
                    height = 100.vh
                    zIndex = 10.unsafeCast<web.cssom.ZIndex>()
                    display = Display.flex
                    flexDirection = FlexDirection.column
                    alignItems = AlignItems.stretch
                    justifyContent = JustifyContent.spaceBetween
                    asDynamic().pointerEvents = "none"
                    asDynamic()["@supports (height: 100dvh)"] =
                        js("{}").unsafeCast<CSSProperties>().apply {
                            asDynamic().height = "100dvh"
                        }
                }
                HeaderBar {
                    logoUrl = data.vRlinkerBO?.companyLogo?.replace("{size}", "original")
                    title = data.title?:""
                }
                ViewerBottomInfoOverlay {
                    propertyInfo = data.vRlinkerBO
                    uid = it.component.uid
                    version = data.version
                    propertyName = data.title
                    propertyLayout = null
                    disclaimerText = null
                    this.scenes = uiState.scenes
                    currentSceneIndex = uiState.activeSceneIndex
                    onSwitchScene = { sceneIndex ->
                        it.component.dispatch(ViewerIntent.SwitchScene(sceneIndex))
                    }
                }
            }
            div {
                css {
                    width = 100.pct
                    height = 100.vh
                    asDynamic()["@supports (height: 100dvh)"] =
                        js("{}").unsafeCast<CSSProperties>().apply {
                            asDynamic().height = "100dvh"
                        }
                }
                PanoramaViewer {
                    this.scenes = uiState.scenes
                    this.activeSceneIndex = uiState.activeSceneIndex
                    this.autorotateEnabled = true
                    this.onSceneChange = { sceneIndex ->
                        it.component.dispatch(ViewerIntent.SwitchScene(sceneIndex))
                    }
                }
            }
        }
    }

    useLayoutEffect(arrayOf(data)) {
        data?.let { panorama -> applyViewerOgMeta(panorama) }
    }
}

/** 与 ogServer 模块 [ogMetaTagsFromPanorama] 规则一致，修改时请同步 */
private fun applyViewerOgMeta(panorama: hk.com.vr360_demo2.webapp.core.net.common.PanoramaDataJsonResult) {
    val companyName = panorama.vRlinkerBO?.companyName ?.let { "$it - " }?: ""
    val title = "${companyName}360全景"
    val description = panorama.title ?: ""
    document.title = title
    setMetaName("description", description)
    setMetaProperty("og:title", title)
    setMetaProperty("og:description", description)
    panorama.cover?.takeIf { it.isNotBlank() }?.let { setMetaProperty("og:image", it) }
    setMetaProperty("og:url", window.location.href)
    setMetaProperty("og:type", "website")
}

private fun setMetaName(name: String, content: String) {
    val meta = document.querySelector("meta[name='$name']")
        ?: document.createElement("meta").also { element ->
            element.setAttribute("name", name)
            document.head?.appendChild(element)
        }
    meta.setAttribute("content", content)
}

private fun setMetaProperty(property: String, content: String) {
    val meta = document.querySelector("meta[property='$property']")
        ?: document.createElement("meta").also { element ->
            element.setAttribute("property", property)
            document.head?.appendChild(element)
        }
    meta.setAttribute("content", content)
}
